// src/utils/surveyUtils.ts
import { arrayMove } from "@dnd-kit/sortable";
import type { SurveyQuestion } from "../types/defaultTypes";

/** Generate a short unique ID */
export const generateId = (length: number = 6): string =>
  Math.random()
    .toString(36)
    .substring(2, 2 + length);

/** Add a new question */
export const addQuestion = (questions: SurveyQuestion[], type: SurveyQuestion["type"]): SurveyQuestion[] => {
  const options = type === "open-ended" ? [] : [{ id: generateId(), answer: "Option 1", order: 1 }];

  return [
    ...questions,
    {
      id: generateId(),
      order: questions.length + 1,
      type,
      text: "",
      options,
      correctAnswers: type === "open-ended" ? [] : [options[0]?.id],
      settings: { maxChars: 0, minChars: 0 },
    },
  ];
};

/** Return labels for a question type */
export const questionTypeLabel = (label: SurveyQuestion["type"]) => {
  if (label === "multi-choice")
    return {
      header: "MULTI SELECTION MCQ",
      answers: "Answer Options (Multiple Selection)",
      type: "checkbox",
    };
  if (label === "open-ended")
    return {
      header: "OPEN ENDED QUESTION",
      answers: "Response Settings",
      type: "radio",
    };
  return {
    header: "SINGLE SELECTION MCQ",
    answers: "Answer Options (Single Selection)",
    type: "radio",
  };
};

/** Update a question by ID */
export const updateQuestion = (
  questions: SurveyQuestion[],
  id: string,
  updates: Partial<SurveyQuestion>
): SurveyQuestion[] => questions.map((q) => (q.id === id ? { ...q, ...updates } : q));

/** Add an option to a question */
export const addOption = (questions: SurveyQuestion[], questionId: string): SurveyQuestion[] =>
  questions.map((q) =>
    q.id === questionId
      ? {
          ...q,
          options: [...(q.options || []), { id: generateId(), answer: "", order: (q.options?.length ?? 0) + 1 }],
        }
      : q
  );

/** Remove an option from a question */
export const removeOption = (questions: SurveyQuestion[], questionId: string, optionId: string): SurveyQuestion[] =>
  questions.map((q) =>
    q.id === questionId
      ? {
          ...q,
          options: q.options?.filter((opt) => opt.id !== optionId).map((opt, idx) => ({ ...opt, order: idx + 1 })),
          correctAnswers: q.correctAnswers.filter((id) => id !== optionId),
        }
      : q
  );

/** Update text of an option */
export const updateOption = (
  questions: SurveyQuestion[],
  questionId: string,
  optionId: string,
  newAnswer: string
): SurveyQuestion[] =>
  questions.map((q) =>
    q.id === questionId
      ? {
          ...q,
          options: q.options?.map((opt) => (opt.id === optionId ? { ...opt, answer: newAnswer } : opt)),
        }
      : q
  );

/** Update correct answer(s) */
export const updateCorrect = (
  questions: SurveyQuestion[],
  questionId: string,
  type: SurveyQuestion["type"],
  optionId: string
): SurveyQuestion[] => {
  if (type === "multi-choice") {
    return questions.map((q) =>
      q.id === questionId
        ? {
            ...q,
            correctAnswers: q.correctAnswers?.includes(optionId)
              ? q.correctAnswers.filter((id) => id !== optionId)
              : [...q.correctAnswers, optionId],
          }
        : q
    );
  }
  return questions.map((q) => (q.id === questionId ? { ...q, correctAnswers: [optionId] } : q));
};

/** Reorder options in a question */
export const reorderOptions = (
  questions: SurveyQuestion[],
  questionId: string,
  activeId: string,
  overId: string
): SurveyQuestion[] =>
  questions.map((q) =>
    q.id === questionId
      ? {
          ...q,
          options: arrayMove(
            q.options || [],
            q.options?.findIndex((o) => o.id === activeId) ?? 0,
            q.options?.findIndex((o) => o.id === overId) ?? 0
          ).map((o, i) => ({ ...o, order: i + 1 })),
        }
      : q
  );
